import is from '@sindresorhus/is';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useContext, useMemo } from 'react';

export type CommentsScope = {
    readonly architectureId: string;
    readonly itemId: string;
    readonly version: number;
    readonly language: string;
};

export const isCommentsScopeCurrent = (
    storedScope: CommentsScope | null,
    currentScope: CommentsScope | null,
): boolean => {
    if (is.null(storedScope)) {
        return true;
    }
    if (is.null(currentScope)) {
        return false;
    }
    return (
        storedScope.architectureId === currentScope.architectureId &&
        storedScope.itemId === currentScope.itemId &&
        storedScope.language === currentScope.language &&
        storedScope.version === currentScope.version
    );
};

export const CommentsScopeContext = createContext<CommentsScope | null>(null);

type CommentsScopeProviderProps = PropsWithChildren<CommentsScope>;

export const CommentsScopeProvider: FunctionComponent<CommentsScopeProviderProps> = ({
    architectureId,
    children,
    itemId,
    language,
    version,
}) => {
    const value = useMemo<CommentsScope>(
        () => ({ architectureId, itemId, language, version }),
        [architectureId, itemId, language, version],
    );
    return <CommentsScopeContext.Provider value={value}>{children}</CommentsScopeContext.Provider>;
};

export const useCommentsScope = (): CommentsScope => {
    const value = useContext(CommentsScopeContext);
    if (is.null(value)) {
        throw new Error('useCommentsScope must be used inside <CommentsScopeProvider>');
    }
    return value;
};

export const useOptionalCommentsScope = (): CommentsScope | null => {
    return useContext(CommentsScopeContext);
};
